import React from 'react';
import { Box, Button, ToggleButton, ToggleButtonGroup, Typography, alpha, useTheme } from '@mui/material';
import { ArrowBackRounded } from '@mui/icons-material';

import useDialogStyles from '../../hooks/useDialogStyles';

// Shared pieces for the VPN edit pages (EditServer, EditServerIP)

// Page title row: back button, round icon, title + subtitle, actions on the right
export const EditPageHeader = ({ icon: Icon, title, subtitle, onBack, children }) => {
  const theme = useTheme();
  const { cancelButtonSx } = useDialogStyles();
  const accent = theme.palette.secondary.main;

  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 2, mb: 2.5 }}>
      <Button onClick={onBack} startIcon={<ArrowBackRounded />} sx={{ ...cancelButtonSx, textTransform: 'none', px: 1.5 }}>
        Back
      </Button>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1, minWidth: 240 }}>
        {Icon && (
          <Box
            sx={{
              width: 44,
              height: 44,
              flexShrink: 0,
              borderRadius: '50%',
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, 0.12),
              border: `1px solid ${alpha(accent, 0.4)}`,
            }}
          >
            <Icon />
          </Box>
        )}
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.secondary[100], lineHeight: 1.2 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography sx={{ fontSize: '0.85rem', color: theme.palette.text.secondary }}>{subtitle}</Typography>
          )}
        </Box>
      </Box>
      {children && <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>{children}</Box>}
    </Box>
  );
};

// Card with a small icon + title header
export const SectionCard = ({ icon: Icon, title, subtitle, action, children, sx }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.alt,
        borderRadius: '0.55rem',
        border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : theme.palette.neutral[200]}`,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 2.5, py: 1.5, borderBottom: `1px solid ${theme.palette.divider}` }}>
        {Icon && (
          <Box
            sx={{
              width: 30,
              height: 30,
              flexShrink: 0,
              borderRadius: 1.5,
              display: 'grid',
              placeItems: 'center',
              color: accent,
              backgroundColor: alpha(accent, isDark ? 0.12 : 0.18),
              '& svg': { fontSize: 18 },
            }}
          >
            <Icon />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, fontSize: '0.95rem' }}>{title}</Typography>
          {subtitle && <Typography variant="caption" sx={{ color: theme.palette.text.secondary }}>{subtitle}</Typography>}
        </Box>
        {action}
      </Box>
      <Box sx={{ p: 2.5, display: 'grid', gap: 2, flex: 1, minHeight: 0 }}>{children}</Box>
    </Box>
  );
};

// Segmented choice (Active / Inactive, target, …) with a caption above it
export const SegmentedField = ({ label, value, options, onChange, disabled }) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const accent = theme.palette.secondary.main;

  return (
    <Box sx={{ minWidth: 0 }}>
      {label && (
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={value}
        disabled={disabled}
        onChange={(e, v) => { if (v !== null) onChange(v); }}
        aria-label={label}
        sx={{
          height: 40,
          p: 0.375,
          gap: 0.5,
          borderRadius: 1.5,
          border: `1px solid ${isDark ? 'rgba(255,255,255,0.28)' : theme.palette.neutral[300]}`,
          backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : theme.palette.background.default,
          '& .MuiToggleButton-root': {
            border: 'none',
            borderRadius: '6px !important',
            textTransform: 'none',
            fontWeight: 600,
            fontSize: '0.8rem',
            color: theme.palette.text.secondary,
            '&:hover': { backgroundColor: alpha(accent, 0.08) },
            '&.Mui-selected, &.Mui-selected:hover': {
              color: accent,
              backgroundColor: alpha(accent, 0.14),
              boxShadow: `inset 0 0 0 1px ${alpha(accent, 0.5)}`,
            },
          },
        }}
      >
        {options.map((o) => (
          <ToggleButton key={o.value} value={o.value}>{o.label}</ToggleButton>
        ))}
      </ToggleButtonGroup>
    </Box>
  );
};
